import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { severityColor } from "@/pages/legal/legalConfig";
import { Button } from "@/components/ui/button";
import {
  FileText, FilePlus2, ClipboardCheck, UploadCloud, ScrollText, ListChecks,
  AlertTriangle, FileSignature, Clock, RefreshCw, ShieldCheck,
} from "lucide-react";

const StatCard = ({ label, value, icon: Icon, tone = "text-cyan-300", testId }) => (
  <div data-testid={testId} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-50">{value ?? 0}</div>
  </div>
);

export default function LegalDashboard() {
  const navigate = useNavigate();
  const { data: d } = useQuery({ queryKey: ["legal-dashboard"], queryFn: async () => (await api.get("/legal/dashboard")).data });
  const { data: a } = useQuery({ queryKey: ["legal-alerts"], queryFn: async () => (await api.get("/legal/alerts")).data });
  const alerts = a?.review_alerts || [];

  const quick = [
    { label: "New Contract", icon: FileSignature, to: "/legal/contracts", testId: "quick-new-contract" },
    { label: "Generate Order Form", icon: FilePlus2, to: "/legal/generator", testId: "quick-generate-order" },
    { label: "Send for Legal Review", icon: ClipboardCheck, to: "/legal/review", testId: "quick-send-review" },
    { label: "Upload Executed Agreement", icon: UploadCloud, to: "/legal/executed", testId: "quick-upload-executed" },
    { label: "Create Template", icon: FileText, to: "/legal/templates", testId: "quick-create-template" },
    { label: "View Legal Register", icon: ListChecks, to: "/legal/register", testId: "quick-view-register" },
  ];

  return (
    <div data-testid="legal-dashboard">
      <PageHeader title="Legal & Contracts" subtitle="Central control centre for templates, contracts and legal review." testId="legal-dashboard-header" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        <StatCard label="Active Contracts" value={d?.active_contracts} icon={FileSignature} testId="stat-active-contracts" />
        <StatCard label="Draft Contracts" value={d?.draft_contracts} icon={FileText} tone="text-slate-300" />
        <StatCard label="Awaiting Signature" value={d?.awaiting_signature} icon={Clock} tone="text-amber-300" />
        <StatCard label="In Negotiation" value={d?.in_negotiation} icon={RefreshCw} tone="text-indigo-300" />
        <StatCard label="Expiring Soon" value={d?.expiring_soon} icon={AlertTriangle} tone="text-amber-300" />
        <StatCard label="Upcoming Renewals" value={d?.upcoming_renewals} icon={RefreshCw} tone="text-sky-300" />
        <StatCard label="Requiring Legal Review" value={d?.agreements_requiring_review} icon={ClipboardCheck} tone="text-amber-300" testId="stat-requiring-review" />
        <StatCard label="Under Review" value={d?.reviews_under_review} icon={ClipboardCheck} tone="text-indigo-300" />
        <StatCard label="Reviews Overdue" value={d?.reviews_overdue} icon={AlertTriangle} tone="text-rose-300" testId="stat-reviews-overdue" />
        <StatCard label="Executed Documents" value={d?.executed_documents} icon={ShieldCheck} tone="text-emerald-300" />
        <StatCard label="Templates" value={d?.templates_total} icon={FileText} testId="stat-templates" />
        <StatCard label="Customers w/o Agreement" value={d?.customers_without_agreements} icon={AlertTriangle} tone="text-slate-300" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionTitle className="mb-3">Action Required</SectionTitle>
          <div className="space-y-2" data-testid="action-required-list">
            {(d?.action_required || []).length === 0 && (
              <p className="rounded-lg border border-dashed border-[#2D3F62] px-4 py-6 text-center text-sm text-slate-500">Nothing needs attention right now.</p>
            )}
            {(d?.action_required || []).map((it, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-[#1E2D4A] bg-[#111A2E]/70 px-4 py-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-slate-200">{it.title}</p>
                  <p className="text-xs text-slate-500">{it.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {alerts.length > 0 && (
            <>
              <SectionTitle className="mb-3 mt-6">Legal Review Reminders</SectionTitle>
              <div className="space-y-2">
                {alerts.map((al, i) => (
                  <div key={i} className={`rounded-lg border px-4 py-2.5 text-sm ${severityColor[al.severity] || severityColor.info}`}>{al.message}</div>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          <SectionTitle className="mb-3">Quick Actions</SectionTitle>
          <div className="grid gap-2">
            {quick.map((q) => (
              <Button key={q.label} variant="outline" data-testid={q.testId}
                className="justify-start border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]"
                onClick={() => navigate(q.to)}>
                <q.icon className="mr-2 h-4 w-4 text-cyan-300" /> {q.label}
              </Button>
            ))}
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-[11px] leading-relaxed text-amber-200/80">
            <ScrollText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            NavScora templates are controlled business documents. A template marked "Legal Review Required" must not be represented as lawyer-approved.
          </div>
        </div>
      </div>
    </div>
  );
}
